import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MalayalamKeyboard from "../components/MalayalamKeyboard";
import PlayPuzzleGrid from "../components/PlayPuzzleGrid";
import axios from "axios";
import { API_URL } from "../components/Constant";
import WordSuggestion from "../components/WordSuggestions";
import {
  Tab,
  Tabs,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
} from "@mui/material";
import EnterAnswerAudio from "../components/EnterAnswerAudio";
import dayjs from "dayjs";
import Grid from "@mui/material/Grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { TimeClock } from "@mui/x-date-pickers/TimeClock";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Colors from "./Colors";
import PlayPuzzleGridBackend from "./PlayPuzzleGridBackend";
import EnterAnswerAudioBackend from "./EnterAnswerAudioBackend";

function PlayPuzzle() {
  const [questions, setQuestions] = useState([]);
  const [highlightedBox, setHighlightedBox] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null); // Timer state
  const [gameStarted, setGameStarted] = useState(false); // Game visibility
  const [publishingTime, setPublishingTime] = useState(null);
  const [serverMode, setServerMode] = useState("Browser");
  const today = dayjs();
  const twoPM = dayjs().set("hour", 14).set("minute", 0);
  const threePM = dayjs().set("hour", 15).set("minute", 0);

  const [allGridData, setAllGridData] = useState([]);

  useEffect(() => {
    localStorage.removeItem("RapidGame");
    const fetchQuestions = async () => {
      try {
        const response = await axios.post(`${API_URL}/questions_temp`, {
          CreatorTocken: localStorage.getItem("CreatorTocken"),
          game_id: localStorage.getItem("gameId"),
        });
        // console.log('response.data',response.data);
        setQuestions(response.data);
        setServerMode(response.data[0].server_mode);
        const dynamicGrid = createDynamicGrid(response.data);
        // console.log("dynamicGrid",dynamicGrid)
        setAllGridData(dynamicGrid);

        // Highlight the box corresponding to the first question on page load
        if (response.data.length > 0) {
          setHighlightedBox(response.data[0]); // Set the first question as highlighted
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchQuestions();
  }, []);

  const fetchQuestionsEC2 = async () => {
    try {
      const response = await axios.post(`${API_URL}/questions_temp_backend`, {
        CreatorTocken: localStorage.getItem("CreatorTocken"),
        gameId: localStorage.getItem("gameId"),
      });

      console.log("response.data", response.data[0].server_mode);
      setQuestions(response.data);
      setServerMode(response.data[0].server_mode);
      const dynamicGrid = createDynamicGrid(response.data);
      //   console.log("dynamicGrid",dynamicGrid)
      setAllGridData(dynamicGrid);

      // Highlight the box corresponding to the first question on page load
      if (response.data.length > 0) {
        setHighlightedBox(response.data[0]); // Set the first question as highlighted
      }
      if (response.data[0].server_mode === "EC2") {
        fetchQuestionsEC2(); // Call fetchQuestionsEC2() and exit
        return;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  function createDynamicGrid(dbResponses) {
    // console.log('dbResponses', dbResponses);

    if (!Array.isArray(dbResponses) || dbResponses.length === 0) {
      console.error("Invalid dbResponses:", dbResponses);
      return [];
    }

    // Step 1: Determine the grid size dynamically
    let rows = 0;
    let cols = 0;

    dbResponses.forEach((data) => {
      const { row_index, col_index, length, direction } = data;

      if (
        typeof row_index !== "number" ||
        typeof col_index !== "number" ||
        typeof length !== "number" ||
        !direction
      ) {
        console.error("Invalid data item:", data);
        return;
      }

      if (direction === "down") {
        rows = Math.max(rows, row_index + length);
        cols = Math.max(cols, col_index + 1);
      } else if (direction === "across") {
        rows = Math.max(rows, row_index + 1);
        cols = Math.max(cols, col_index + length);
      }
    });

    // Step 2: Initialize the empty grid with correct dimensions
    const grid = Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => ({
        value: "",
        visible: false,
      })),
    );

    // console.log('Initialized grid:', grid);

    // Step 3: Populate the grid with questions and visibility
    dbResponses.forEach((data) => {
      const { row_index, col_index, direction, length, question_number } = data;

      if (
        typeof row_index !== "number" ||
        typeof col_index !== "number" ||
        typeof length !== "number" ||
        !direction
      ) {
        console.error("Invalid data item during grid population:", data);
        return;
      }

      if (direction === "down") {
        // Fill cells vertically for "down" direction
        for (let i = 0; i < length; i++) {
          const row = grid[row_index + i];
          if (row && row[col_index]) {
            row[col_index] = {
              value: i === 0 ? question_number.toString() : "",
              visible: true,
            };
          } else {
            console.error(
              `Out of bounds: row_index=${row_index + i}, col_index=${col_index}`,
            );
          }
        }
      } else if (direction === "across") {
        // Fill cells horizontally for "across" direction
        for (let i = 0; i < length; i++) {
          const cell = grid[row_index]?.[col_index + i];
          if (cell) {
            grid[row_index][col_index + i] = {
              value: i === 0 ? question_number.toString() : "",
              visible: true,
            };
          } else {
            console.error(
              `Out of bounds: row_index=${row_index}, col_index=${col_index + i}`,
            );
          }
        }
      }
    });

    console.log("Populated grid:", grid);
    return grid;
  }

  const handleCellClick = (question) => {
    setHighlightedBox(question);
    setCurrentQuestionIndex(question.question_number - 1);
  };

  const handleNext = () => {
    let nextIndex = currentQuestionIndex + 1;

    // Loop until we find the next question with valid colIndex & rowIndex
    while (
      nextIndex < questions.length &&
      (questions[nextIndex]?.col_index === null ||
        questions[nextIndex]?.row_index === null)
    ) {
      nextIndex++;
    }

    // If we've found a valid index, set it
    if (nextIndex < questions.length) {
      setCurrentQuestionIndex(nextIndex);
      setHighlightedBox(questions[nextIndex]);
    }
  };

  const handlePrevious = () => {
    let prevIndex = currentQuestionIndex - 1;

    // Loop until we find the previous question with valid colIndex & rowIndex
    while (
      prevIndex >= 0 &&
      (questions[prevIndex]?.col_index === null ||
        questions[prevIndex]?.row_index === null)
    ) {
      prevIndex--;
    }

    // If we've found a valid index, set it
    if (prevIndex >= 0) {
      setCurrentQuestionIndex(prevIndex);
      setHighlightedBox(questions[prevIndex]);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue); // Update tab index
  };

  const handleQuestionClick = (question, index) => {
    handleCellClick({
      question_number: question.question_number,
      direction: question.direction,
      question_text: question.question_text,
      answer: question.answer,
      length: question.length,
    });
    setTabIndex(0); // Switch to the "Active Question" tab
    setCurrentQuestionIndex(question.question_number - 1); // Set the index of the selected question
  };

  const renderAttachment = (url) => {
    if (!url) return null;

    // Handle Image Thumbnail
    if (
      url.match(
        /\.(apng|png|avif|gif|jpg|jpeg|jfif|pjpeg|pjp|svg|webp|pms|svg)$/i,
      )
    ) {
      return (
        <img
          key={url}
          src={url}
          alt="Attachment"
          style={{
            width: "120px",
            height: "120px",
            objectFit: "100% 100%",
            borderRadius: "4px",
          }}
        />
      );
    }

    // Handle Audio - Show as a small icon
    if (url.match(/\.(mp3|wav|aac|ogg|flac|m4a|wma|opus)$/i)) {
      return (
        <audio controls style={{ width: "100px" }} src={url}>
          Your browser does not support the audio tag.
        </audio>
      );
    }

    // Handle Video Thumbnail Previews
    if (url.match(/\.(mp4|webm|avi|mkv|mov|wmv|flv|mpeg|mpg|3gp)$/i)) {
      return (
        <video
          key={url}
          controls
          style={{
            width: "80%",
            height: "220px",
            objectFit: "cover",
            borderRadius: "4px",
          }}
        >
          <source src={url} />
          Your browser does not support the video tag.
        </video>
      );
    }

    return null;
  };

  const [open, setOpen] = useState(false); // Modal open state
  const [isSchedule, setIsSchedule] = useState(false); // Determines if we are in schedule mode
  const [selectedDate, setSelectedDate] = useState(dayjs()); // Date state for the picker
  const [openClockDialog, setOpenClockDialog] = useState(false);
  const [publishTime, setPublishTime] = useState(dayjs());

  const handleClockOpen = () => setOpenClockDialog(true);
  const handleClockClose = () => setOpenClockDialog(false);
  // Handle opening the modal
  const handleOpen = () => {
    setOpen(true);
    setIsSchedule(false); // Reset back to initial state
  };

  // Handle closing the modal
  const handleClose = () => {
    setOpen(false);
    setIsSchedule(false); // Reset to the default state
  };

  const handleTimeChange = (time) => {
    setSelectedDate(time);
  };

  const handleAMPMChange = (ampm) => {
    const updatedTime =
      ampm === "AM"
        ? selectedDate.hour(selectedDate.hour() % 12) // Keep hour within 0-11
        : selectedDate.hour((selectedDate.hour() % 12) + 12); // Adjust to 12-23
    setSelectedDate(updatedTime);
    handleClockClose(); // Close the dialog after AM/PM selection
  };

  const handleReleaseGame = async () => {
    const formattedDate = dayjs(selectedDate.$d).format(
      "YYYY-MM-DD HH:mm:ss.SSS",
    );
    // console.log(formattedDate)
    try {
      const choosedqns = questions.filter(
        (q) => q.row_index !== null && q.col_index !== null,
      );
      const response = await axios.post(`${API_URL}/publish_game`, {
        gameId: localStorage.getItem("gameId"),
        CreatorTocken: localStorage.getItem("CreatorTocken"),
        questions: choosedqns,
        publishtime: formattedDate,
        theme: localStorage.getItem("theme"),
        category: localStorage.getItem("category"),
        serverType: localStorage.getItem("server"),
      });
      // console.log('response.data',response.data);

      if (response.data.success === true) {
        window.location.href = "creator-landing";
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: Colors.gamebg,
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          gap: "12px",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          maxWidth: "100%",
        }}
      >
        {questions.length > 0 && (
          <>
            <Button
              onClick={handleOpen}
              sx={{
                background: Colors.button1,
                color: Colors.button1font,
                width: "250px",
                marginTop: "2.5%",
                border: "1px solid ",
                borderColor: Colors.button1Border,
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "12px",
                transition: "background 0.3s, transform 0.3s",
                "&:hover": {
                  background: Colors.button1,
                  transform: "scale(1.05)",
                },
                "&:active": {
                  background: Colors.button1,
                },
              }}
            >
              Release Game
            </Button>

            <Dialog open={open} onClose={handleClose}>
              <DialogTitle>Are you ready to release the game?</DialogTitle>
              <DialogContent>
                {!isSchedule ? (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "16px",
                      }}
                    >
                      <Button
                        onClick={() => {
                          setPublishTime(
                            dayjs()
                              .add(5, "minute")
                              .format("YYYY-MM-DD HH:mm A"),
                          );
                          const formattedDate = dayjs().add(5, "minute");
                          setSelectedDate(formattedDate);
                          handleReleaseGame();
                          handleClose();
                        }}
                        sx={{
                          background: Colors.button1,
                          color: Colors.button1font,
                          width: "120px",
                          border: "1px solid ",
                          borderColor: Colors.button1Border,
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "12px",
                          "&:hover": {
                            background: Colors.button1,
                          },
                          "&:active": {
                            background: Colors.button1,
                          },
                        }}
                      >
                        Release Now
                      </Button>
                      <Button
                        onClick={() => setIsSchedule(true)}
                        sx={{
                          background: Colors.button2,
                          color: Colors.button2font,
                          width: "120px",
                          border: "1px solid",
                          borderColor: Colors.button2Border,
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "12px",

                          "&:hover": {
                            background: Colors.button2,
                          },
                          "&:active": {
                            background: Colors.button2,
                          },
                        }}
                      >
                        Schedule
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Grid
                        container
                        spacing={3}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid item>
                          <DatePicker
                            value={selectedDate}
                            onChange={(date) => {
                              if (date && date.isValid()) {
                                setSelectedDate(dayjs(date)); // Convert to Day.js if needed
                              }
                            }}
                            disablePast
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Date"
                                value={selectedDate.format("DD-MM-YYYY")}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>{params.InputProps?.endAdornment}</>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item>
                          <TextField
                            value={selectedDate.format("hh:mm A")}
                            InputProps={{
                              endAdornment: (
                                <IconButton onClick={handleClockOpen}>
                                  <AccessTimeIcon />
                                </IconButton>
                              ),
                            }}
                            readOnly
                            fullWidth
                          />
                        </Grid>
                      </Grid>

                      <Dialog open={openClockDialog} onClose={handleClockClose}>
                        <DialogContent>
                          <TimeClock
                            minTime={
                              dayjs(selectedDate).isSame(dayjs(), "day") // If the selected date is today
                                ? dayjs() // Disable past time (current time onwards)
                                : dayjs(selectedDate).startOf("day") // If the selected date is tomorrow or later, disable before that date
                            }
                            value={selectedDate}
                            onChange={(time) => {
                              if (time && time.isValid()) {
                                setSelectedDate(time); // Update time in the selectedDate state
                              }
                            }}
                            ampm={false} // Disable built-in AM/PM toggle
                          />
                          {/* Custom AM/PM Buttons */}
                          <Grid
                            container
                            justifyContent="center"
                            spacing={2}
                            style={{ marginTop: 16 }}
                          >
                            <Grid item>
                              <Button
                                variant={
                                  selectedDate.hour() < 12
                                    ? "contained"
                                    : "outlined"
                                }
                                onClick={() => handleAMPMChange("AM")}
                              >
                                AM
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                variant={
                                  selectedDate.hour() >= 12
                                    ? "contained"
                                    : "outlined"
                                }
                                onClick={() => handleAMPMChange("PM")}
                              >
                                PM
                              </Button>
                            </Grid>
                          </Grid>
                        </DialogContent>
                      </Dialog>
                    </LocalizationProvider>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} sx={{ color: Colors.tab1color }}>
                  Cancel
                </Button>
                {isSchedule && (
                  <Button
                    onClick={() => {
                      setPublishTime(
                        selectedDate.format("YYYY-MM-DD HH:mm:ss"),
                      );
                      handleReleaseGame();
                      handleClose();
                    }}
                    sx={{ color: Colors.tab2color }}
                  >
                    Confirm
                  </Button>
                )}
              </DialogActions>
            </Dialog>

            {/* <PlayPuzzleGrid
            highlightedBox={highlightedBox}
            onCellClick={handleCellClick}
            questions={questions}
            userAnswer={userAnswer}
            setHighlightedBox={setHighlightedBox}
            setUserAnswer={setUserAnswer}
            allGridData ={allGridData}
            setAllGridData={setAllGridData}
          /> */}
            {serverMode === "EC2" ? (
              <PlayPuzzleGridBackend
                highlightedBox={highlightedBox}
                onCellClick={handleCellClick}
                questions={questions}
                userAnswer={userAnswer}
                setHighlightedBox={setHighlightedBox}
                setUserAnswer={setUserAnswer}
                allGridData={allGridData}
                setAllGridData={setAllGridData}
              />
            ) : (
              <PlayPuzzleGrid
                highlightedBox={highlightedBox}
                onCellClick={handleCellClick}
                questions={questions}
                userAnswer={userAnswer}
                setHighlightedBox={setHighlightedBox}
                setUserAnswer={setUserAnswer}
                allGridData={allGridData}
                setAllGridData={setAllGridData}
              />
            )}
          </>
        )}

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          centered
          sx={{
            ".MuiTabs-indicator": {
              display: "none", // Hide the default indicator
            },
            display: "flex",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          <Tab
            label="Active Question"
            sx={{
              fontSize: "14px",
              color: Colors.tab1color,
              textDecoration: "none",
              textUnderlineOffset: "5px",
              "&:hover": {
                textDecoration: "underline", // Apply underline on hover
                textUnderlineOffset: "5px",
                textDecorationThickness: "1.5px",
              },
              "&.Mui-selected": {
                color: Colors.tab1color, // Keep the selected tab styled
                textDecoration: "underline", // Underline for the active tab
                textUnderlineOffset: "5px",
                textDecorationThickness: "1.5px", // Make the underline bold for the active tab
              },
            }}
          />
          <Tab
            label="All Questions"
            sx={{
              fontSize: "14px",
              color: Colors.tab2color,
              textDecoration: "none",
              textUnderlineOffset: "5px",
              "&:hover": {
                textDecoration: "underline", // Apply underline on hover
                textUnderlineOffset: "5px",
                textDecorationThickness: "1.5px",
              },
              "&.Mui-selected": {
                color: Colors.tab2color, // Keep the selected tab styled
                fontWeight: "bold",
                textDecoration: "underline", // Underline for the active tab
                textUnderlineOffset: "5px",
                textDecorationThickness: "1.5px", // Make the underline bold for the active tab
              },
            }}
          />
        </Tabs>

        {tabIndex === 0 && (
          <div>
            {highlightedBox ? (
              <Box
                sx={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <h2 style={{ lineHeight: "0px" }}>
                  {highlightedBox.direction === "down"
                    ? "താഴോട്ട്"
                    : "വലത്തോട്ട്"}
                </h2>
                <Box
                  sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <p>
                    {highlightedBox.question_number}.{" "}
                    {highlightedBox.question_text}{" "}
                  </p>

                  {highlightedBox.attachment &&
                    renderAttachment(highlightedBox.attachment)}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Button
                    sx={{
                      background: "none",
                      color: "#5BC8AC",
                      fontSize: "14px",
                      textDecoration: "none",
                      textUnderlineOffset: "5px",
                      "&:hover": {
                        background: "none",
                        color: "#5BC8AC",
                        fontSize: "14px",
                        textDecoration: "underline", // Apply underline on hover
                        textUnderlineOffset: "5px",
                        textDecorationThickness: "1.5px",
                      },
                    }}
                    variant="text"
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0} // Disable if it's the first question
                  >
                    Previous
                  </Button>

                  <Button
                    sx={{
                      background: "none",
                      color: "#F18D9E",
                      fontSize: "14px",
                      textDecoration: "none",
                      textUnderlineOffset: "5px",
                      "&:hover": {
                        background: "none",
                        color: "#F18D9E",
                        fontSize: "14px",
                        textDecoration: "underline", // Apply underline on hover
                        textUnderlineOffset: "5px",
                        textDecorationThickness: "1.5px",
                      },
                    }}
                    variant="text"
                    color="primary"
                    onClick={handleNext}
                    disabled={currentQuestionIndex === questions.length - 1} // Disable if it's the last question
                  >
                    Next
                  </Button>
                </Box>

                {/* <EnterAnswerAudio
                  highlightedBox={highlightedBox}
                  allQuestions={questions}
                  setHighlightedBox={setHighlightedBox}
                  setUserAnswer={setUserAnswer}
                  allGridData ={allGridData}
                  setAllGridData={setAllGridData}
                /> */}
                {serverMode === "EC2" ? (
                  <EnterAnswerAudioBackend
                    highlightedBox={highlightedBox}
                    allQuestions={questions}
                    setHighlightedBox={setHighlightedBox}
                    setUserAnswer={setUserAnswer}
                    allGridData={allGridData}
                    setAllGridData={setAllGridData}
                    setCurrentQuestionIndex={setCurrentQuestionIndex}
                    currentQuestionIndex={currentQuestionIndex}
                  />
                ) : (
                  <EnterAnswerAudio
                    highlightedBox={highlightedBox}
                    allQuestions={questions}
                    setHighlightedBox={setHighlightedBox}
                    setUserAnswer={setUserAnswer}
                    allGridData={allGridData}
                    setAllGridData={setAllGridData}
                  />
                )}
              </Box>
            ) : (
              <p>ക്ഷമയോടെ കാത്തിരിക്കുക...</p>
            )}
          </div>
        )}

        {tabIndex === 1 && (
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              flexDirection: "column",
              marginBottom: "64px",
            }}
          >
            <h3>Question List</h3>
            <h5>Placed Questions</h5>
            {questions
              .filter((q) => q.row_index !== null && q.col_index !== null) // Filter only questions with row_index and col_index
              .map((q, index) => (
                <div
                  key={index}
                  onClick={() => handleQuestionClick(q, index)} // Make each question clickable
                  style={{
                    cursor: "pointer",
                    marginBottom: "8px",
                    transition: "color 0.3s ease", // Smooth transition for the color change
                  }}
                  onMouseEnter={(e) => (e.target.style.color = "#32618D")} // Change color on hover
                  onMouseLeave={(e) => (e.target.style.color = "initial")} // Reset color when hover ends
                >
                  <Typography sx={{ fontSize: "14px", fontFamily: "Roboto" }}>
                    {q.question_number}. {q.question_text}
                  </Typography>
                  {q.attachment && renderAttachment(q.attachment)}
                </div>
              ))}
            <h5>Unplaced Questions</h5>
            {questions
              .filter((q) => q.row_index === null && q.col_index === null) // Filter only questions with row_index and col_index
              .map((q, index) => (
                <div
                  key={index}
                  onClick={() => handleQuestionClick(q, index)} // Make each question clickable
                  style={{
                    cursor: "pointer",
                    marginBottom: "8px",
                    transition: "color 0.3s ease", // Smooth transition for the color change
                  }}
                  onMouseEnter={(e) => (e.target.style.color = "#32618D")} // Change color on hover
                  onMouseLeave={(e) => (e.target.style.color = "initial")} // Reset color when hover ends
                >
                  <Typography sx={{ fontSize: "14px", fontFamily: "Roboto" }}>
                    {q.question_number}. {q.question_text}
                  </Typography>

                  {q.attachment && renderAttachment(q.attachment)}
                </div>
              ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PlayPuzzle;
